import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter } from '@angular/core';

import { isNullOrUndefined } from '@app/util/util';
import { AssetChubbBookingBannerInterface } from '@app/entities/asset-chubb-booking-banner';
import {
  AssistanceSearchInclusion,
  AssistanceSearchResponse, AssistanceSearchResult
} from '@shared/travel-assistance/entities/booking-assistance';
import { AssetChubbBookingBanner } from '@asset-chubb-booking-banner';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { CurrencyFormatPipe } from '@app/pipes/currency-format.pipe';
import { assistanceHighlightedBanner } from '@shared/travel-assistance/mappers/assistance-highlighted.mapper';
import { isActiveByPrimeCriteriaFree } from '@ux/mappers/assistance-banner.mapper';
import { environment as ENV } from '@environment';
import { discountPercentageCalculatedByTaxes } from '@app/mappers/discount-percentage.mapper';

@Component({
  selector: 'app-chubb-booking-banner',
  templateUrl: './chubb-booking-banner.component.html',
  styleUrls: ['./chubb-booking-banner.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ChubbBookingBannerComponent {

  @Input() bookingType: TravelBookingType;
  @Input() isInternationalTrip: boolean;
  @Input() days: number;
  @Input() travelers: number;
  @Input() isPayment: boolean;
  @Input() assistanceInfo: AssistanceSearchResponse;
  @Input() isActivateFreeChubbAssistance: boolean;
  @Input() isPrimeUser: boolean;

  @Output() showPaymentOfferDetails: EventEmitter<void> = new EventEmitter<void>();

  public readonly CHUBB_BOOKING_IMAGES_BASE_PATH: string = 'web/travel/Chubb/Booking';
  public readonly CHUBB_LOGO: string = 'chubb-logo.png';
  public readonly CHUBB_CHECK: string = 'icons-general-check.png';
  public readonly CHUBB_WELCOME: string = 'img-welcome.png';

  constructor(
    private currencyFormatPipe: CurrencyFormatPipe
  ) { }

  get assetChubbBookingBanner(): AssetChubbBookingBannerInterface {
    return AssetChubbBookingBanner;
  }

  get isEnabledByBookingType(): boolean {
    return this.isPayment ? true : this.isActivateFreeChubbAssistance;
  }

  get isEnabledGiftPrimeTag(): boolean {
    return this.isActivateFreeChubbAssistance && isActiveByPrimeCriteriaFree(this.isPrimeUser);
  }

  get lateralBanner(): string {
    return this.assetChubbBookingBanner?.LATERAL_BANNER;
  }

  get options(): string[] {
    if (this.isInternationalTrip) {
      return this.assetChubbBookingBanner?.INTERNATIONAL_COPY_OPTIONS || [];
    } else return this.assetChubbBookingBanner?.NATIONAL_COPY_OPTIONS || [];
  }

  get hasDaysAndTravelers(): boolean {
    return !!this.days && !!this.travelers;
  }

  get isPluralStay(): boolean {
    return this.days > 1;
  }

  get isPluralTravelers(): boolean {
    return this.travelers > 1;
  }

  get stayAndTravelersLabel(): string {
    let label = '';
    if (this.isPluralTravelers) {
      label = this.assetChubbBookingBanner.COUNTER_BANNER_WITH_PLURAL;
    } else {
      label = this.assetChubbBookingBanner.COUNTER_BANNER_WITH_SINGLE;
    }
    label = label.replace('{{days}}', this.days?.toString());
    label = label.replace('{{type}}', this.typeStay?.toString());
    label = label.replace('{{travelers}}', this.travelers?.toString());
    return label;
  }

  get paymentPrice(): string {
    if (!isNullOrUndefined(this.resultOption?.TotalPrice?.Total)) {
      return this.currencyFormatPipe.transform(this.resultOption?.TotalPrice?.Total, this.resultOption?.TotalPrice?.CurrencyCode);
    } else {
      return '' 
    }
  }

  get hasDiscount(): boolean {
    return this.resultOption?.TotalPrice?.DiscountAmount > 0;
  }

  get priceWithoutDiscount(): number {
    return (this.resultOption?.TotalPrice?.Taxes as number || 0) + (this.resultOption?.TotalPrice?.Amount || 0)
      + (this.resultOption?.TotalPrice?.Fees || 0) + (this.resultOption?.TotalPrice?.Surcharges || 0);
  }
  
  get currencyCode(): string {
    return this.resultOption?.TotalPrice?.CurrencyCode;
  }
  
  get discountPercentage(): number {
    return discountPercentageCalculatedByTaxes(
      this.resultOption?.TotalPrice?.DiscountAmount,
      this.resultOption?.TotalPrice?.Amount,
      this.resultOption?.TotalPrice?.Taxes as number
    );
  }

  get assistanceHighlighted(): AssistanceSearchInclusion[] {
    if (!isNullOrUndefined(this.resultOption)) {
      return assistanceHighlightedBanner(this.resultOption);
    } else return [];
  }

  private get typeStay(): string {
    switch (this.bookingType) {
      case TravelBookingType.HOTELS:
        return this.isPluralStay ? this.assetChubbBookingBanner.NIGHTS :
          this.assetChubbBookingBanner.NIGHT;
      default:
        return this.isPluralStay ? this.assetChubbBookingBanner.DAYS :
          this.assetChubbBookingBanner.DAY;
    }
  }

  private get resultOption(): AssistanceSearchResult {
    return this.assistanceInfo?.Results?.[0];
  }

  public showPaymentOfferDetailsEvent(): void {
    this.showPaymentOfferDetails.emit();
  }

  public trackBy(index: number): number {
    return index;
  }

  public isInsuredSumVariesAge(isLast: boolean): boolean {
    return isLast && !!ENV.chubb_insured_sum_age;
  }

}
